import React from 'react';
import { Box, Grid, Tooltip, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useLocation, useNavigate } from 'react-router-dom';
import { Bar } from 'react-chartjs-2';

import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ViewWeekIcon from '@mui/icons-material/ViewWeek';
import ScheduleIcon from '@mui/icons-material/Schedule';

import { MIN_PERIOD_ROUNDS, PeriodSummary } from '../data/period_insights';
import { getTooltipDefaults } from '../charts/helpers';
import { DS, Overline, enterSx, formatSigned, numericSx, trendColor } from './Dashboard';

/*****************************************************************************
 ** Sub navigation between the three period views
*****************************************************************************/

const PERIOD_TABS = [
    { path: '/period', label: 'Calendar', icon: <CalendarMonthIcon /> },
    { path: '/period/daily', label: 'Day of week', icon: <ViewWeekIcon /> },
    { path: '/period/hourly', label: 'Time of day', icon: <ScheduleIcon /> },
];

export const PeriodNav: React.FC<{ title: string; subtitle: string }> = ({ title, subtitle }) => {
    const { pathname } = useLocation();
    const navigate = useNavigate();

    return (
        <Grid item xs={12} sx={enterSx(0)}>
            <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                <Box>
                    <Overline>By period</Overline>
                    <Typography sx={{ fontSize: { xs: '24px', sm: '28px' }, fontWeight: 800, letterSpacing: '-0.02em', color: DS.text, lineHeight: 1.2 }}>
                        {title}
                    </Typography>
                    <Typography sx={{ fontSize: '14px', color: DS.textMuted, mt: 0.5 }}>{subtitle}</Typography>
                </Box>
                <Box role="tablist" sx={{
                    display: 'flex',
                    p: 0.5,
                    gap: 0.5,
                    borderRadius: '12px',
                    background: DS.surface,
                    border: `1px solid ${DS.border}`,
                    maxWidth: '100%',
                    overflowX: 'auto',
                }}>
                    {PERIOD_TABS.map(tab => {
                        const active = pathname === tab.path;
                        return (
                            <Box
                                key={tab.path}
                                role="tab"
                                aria-selected={active}
                                tabIndex={0}
                                onClick={() => navigate(tab.path)}
                                onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') navigate(tab.path); }}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.75,
                                    px: 1.5,
                                    py: 0.75,
                                    borderRadius: '9px',
                                    cursor: 'pointer',
                                    whiteSpace: 'nowrap',
                                    fontSize: '13px',
                                    fontWeight: 600,
                                    color: active ? DS.text : DS.textMuted,
                                    background: active ? alpha(DS.brand, 0.18) : 'transparent',
                                    transition: 'background 0.15s, color 0.15s',
                                    '&:hover': { color: DS.text, background: active ? alpha(DS.brand, 0.18) : DS.inset },
                                    '& svg': { fontSize: 16, color: active ? DS.brand : 'inherit' },
                                }}
                            >
                                {tab.icon}
                                {tab.label}
                            </Box>
                        );
                    })}
                </Box>
            </Box>
        </Grid>
    );
};

/*****************************************************************************
 ** Highlight card: best / toughest / busiest period
*****************************************************************************/

interface InsightCardProps {
    overline: string;
    accent: string;
    period?: PeriodSummary;
    metric: 'avg' | 'count' | 'total';
    emptyText: string;
    delay?: number;
}

export const InsightCard: React.FC<InsightCardProps> = ({ overline, accent, period, metric, emptyText, delay = 0 }) => (
    <Grid item xs={12} sm={4}>
        <Box sx={{
            height: '100%',
            p: { xs: 2, sm: 2.5 },
            borderRadius: DS.radius,
            background: `linear-gradient(180deg, ${alpha(accent, 0.1)} 0%, transparent 70%), ${DS.surface}`,
            border: `1px solid ${alpha(accent, 0.28)}`,
            ...enterSx(delay),
        }}>
            <Overline color={accent}>{overline}</Overline>
            {period ? (
                <>
                    <Typography sx={{ fontSize: '22px', fontWeight: 800, color: DS.text, mt: 0.5, lineHeight: 1.25 }}>
                        {period.label}
                    </Typography>
                    <Typography sx={{ fontSize: '14px', fontWeight: 700, color: metric === 'avg' ? trendColor(period.avgChange) : metric === 'total' ? trendColor(period.totalChange) : DS.text, mt: 0.5, ...numericSx }}>
                        {metric === 'avg'
                            ? `${formatSigned(period.avgChange, 1)} Elo per round`
                            : metric === 'total'
                                ? `${formatSigned(period.totalChange)} rating that day`
                                : `${period.count} rounds · ${period.share}% of your play`}
                    </Typography>
                    <Typography sx={{ fontSize: '12px', color: DS.textMuted, mt: 0.25, ...numericSx }}>
                        {metric === 'count'
                            ? `${formatSigned(period.avgChange, 1)} Elo per round`
                            : `${period.count} rounds · gained rating in ${period.gainPercent}%`}
                    </Typography>
                </>
            ) : (
                <Typography sx={{ fontSize: '13px', color: DS.textMuted, mt: 1 }}>{emptyText}</Typography>
            )}
        </Box>
    </Grid>
);

/*****************************************************************************
 ** Gain / even / loss split
*****************************************************************************/

export const OutcomeSplit: React.FC<{ period: PeriodSummary; height?: number }> = ({ period, height = 6 }) => (
    <Tooltip arrow title={`Gained ${period.gainPercent}% · Even ${period.evenPercent}% · Lost ${period.lossPercent}%`}>
        <Box sx={{ display: 'flex', height, gap: '2px', borderRadius: '999px', overflow: 'hidden', background: DS.inset }}>
            {period.count > 0 && (
                <>
                    <Box sx={{ width: `${period.gainPercent}%`, background: DS.gain }} />
                    <Box sx={{ width: `${period.evenPercent}%`, background: DS.neutral }} />
                    <Box sx={{ width: `${period.lossPercent}%`, background: DS.loss }} />
                </>
            )}
        </Box>
    </Tooltip>
);

/*****************************************************************************
 ** Tile for one period: avg Elo, volume and outcome split
*****************************************************************************/

export const PeriodTile: React.FC<{ period: PeriodSummary; highlight?: 'best' | 'toughest'; delay: number }> = ({ period, highlight, delay }) => {
    const played = period.count > 0;
    const accent = highlight === 'best' ? DS.gain : highlight === 'toughest' ? DS.loss : undefined;
    return (
        <Box sx={{
            p: 2,
            borderRadius: '12px',
            background: DS.inset,
            border: `1px solid ${accent ?? DS.border}`,
            opacity: played ? 1 : 0.55,
            ...enterSx(delay),
        }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                <Overline>{period.label}</Overline>
                {highlight && (
                    <Typography sx={{ fontSize: '11px', fontWeight: 700, color: accent }}>
                        {highlight === 'best' ? 'Best' : 'Toughest'}
                    </Typography>
                )}
            </Box>
            <Typography sx={{ fontSize: '26px', fontWeight: 800, lineHeight: 1.2, mt: 0.5, color: played ? trendColor(period.avgChange) : DS.textFaint, ...numericSx }}>
                {played ? formatSigned(period.avgChange, 1) : '—'}
            </Typography>
            <Typography sx={{ fontSize: '12px', color: DS.textMuted, mb: 1.25, ...numericSx }}>
                {played ? `Elo / round · ${period.count} rounds (${period.share}%)` : 'No rounds played'}
            </Typography>
            <OutcomeSplit period={period} />
            {played && (
                <Typography sx={{ fontSize: '11px', color: period.reliable ? DS.textFaint : DS.warning, mt: 0.75, ...numericSx }}>
                    {period.reliable
                        ? `Gained ${period.gainPercent}% · Lost ${period.lossPercent}%`
                        : `Small sample, under ${MIN_PERIOD_ROUNDS} rounds`}
                </Typography>
            )}
        </Box>
    );
};

/*****************************************************************************
 ** Average Elo per round, one bar per period
*****************************************************************************/

export const AvgChangeBarChart: React.FC<{ periods: PeriodSummary[]; labels: string[]; height?: number }> = ({ periods, labels, height = 280 }) => (
    <Box sx={{ height }}>
        <Bar
            data={{
                labels,
                datasets: [{
                    data: periods.map(p => p.avgChange),
                    backgroundColor: periods.map(p => alpha(trendColor(p.avgChange), p.reliable ? 0.85 : 0.3)),
                    hoverBackgroundColor: periods.map(p => trendColor(p.avgChange)),
                    borderRadius: 6,
                    borderSkipped: false,
                    maxBarThickness: 44,
                }],
            }}
            options={{
                responsive: true,
                maintainAspectRatio: false,
                animation: { duration: 500 },
                plugins: {
                    legend: { display: false },
                    tooltip: {
                        ...getTooltipDefaults({ palette: { text: { primary: DS.text, secondary: DS.textMuted }, background: { default: '#161c24' } } }),
                        padding: 12,
                        borderColor: DS.borderStrong,
                        borderWidth: 1,
                        displayColors: false,
                        callbacks: {
                            title: (items) => periods[items[0].dataIndex].label,
                            label: (item) => {
                                const p = periods[item.dataIndex];
                                if (!p.count) return 'No rounds played';
                                return [
                                    `Elo per round: ${formatSigned(p.avgChange, 1)}`,
                                    `Rounds: ${p.count} (${p.share}%)`,
                                    `Gained ${p.gainPercent}% · Lost ${p.lossPercent}%`,
                                    ...(p.reliable ? [] : [`Fewer than ${MIN_PERIOD_ROUNDS} rounds`]),
                                ];
                            },
                        },
                    },
                    annotation: {
                        annotations: {
                            zero: { type: 'line', yMin: 0, yMax: 0, borderColor: DS.borderStrong, borderWidth: 1 },
                        },
                    },
                },
                scales: {
                    y: {
                        border: { display: false },
                        grid: { color: 'rgba(145,158,171,0.08)' },
                        ticks: { color: DS.textMuted, font: { size: 11 }, maxTicksLimit: 6, callback: (v) => formatSigned(Number(v), 1) },
                    },
                    x: {
                        border: { display: false },
                        grid: { display: false },
                        ticks: { color: DS.textMuted, font: { size: 11 }, autoSkip: true, maxRotation: 0 },
                    },
                },
            }}
        />
    </Box>
);

export const ChartLegendNote: React.FC = () => (
    <Box sx={{ display: 'flex', gap: 2.5, flexWrap: 'wrap', mb: 1.5 }}>
        {[
            { color: DS.gain, label: 'Gaining rating' },
            { color: DS.loss, label: 'Losing rating' },
            { color: alpha(DS.neutral, 0.4), label: `Fewer than ${MIN_PERIOD_ROUNDS} rounds` },
        ].map(({ color, label }) => (
            <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Box sx={{ width: 10, height: 10, borderRadius: '3px', background: color }} />
                <Typography sx={{ fontSize: '12px', color: DS.textMuted }}>{label}</Typography>
            </Box>
        ))}
    </Box>
);
